/**
 * 打印模板 font-family：页面默认 + 基本信息/表格列可覆盖。
 * 存库为 CSS font-family 取值（勿加 `font-family:` 前缀）；空字符串表示「未指定」。
 */

/**
 * 可选字体预设（各板块下拉共用；value 为完整 font-family 栈）。
 * richTextName：富文本工具栏 execCommand('fontName') 用的主字体名。
 */
export const PRINT_FONT_FAMILY_PRESETS = [
  { label: '宋体·针式清晰', value: "'SimSun','NSimSun','Songti SC','STSong',serif", richTextName: 'SimSun' },
  { label: '仿宋', value: "'FangSong','STFangsong','SimSun',serif", richTextName: 'FangSong' },
  { label: '楷体', value: "'KaiTi','STKaiti','SimKai',serif", richTextName: 'KaiTi' },
  { label: '黑体', value: "'SimHei','Microsoft YaHei','PingFang SC',sans-serif", richTextName: 'SimHei' },
  {
    label: '微软雅黑',
    value: "'Microsoft YaHei','PingFang SC','Helvetica Neue',Arial,sans-serif",
    richTextName: 'Microsoft YaHei'
  },
  {
    label: '苹方 / 思源黑体',
    value: "'PingFang SC','Source Han Sans SC','Noto Sans CJK SC','Microsoft YaHei',sans-serif",
    richTextName: 'PingFang SC'
  },
  { label: 'Arial', value: "Arial,Helvetica,'Helvetica Neue',sans-serif", richTextName: 'Arial' },
  { label: 'Helvetica', value: "Helvetica,Arial,'Helvetica Neue',sans-serif", richTextName: 'Helvetica' },
  {
    label: 'Times New Roman',
    value: "'Times New Roman',Times,'Songti SC',serif",
    richTextName: 'Times New Roman'
  },
  { label: 'Calibri', value: "Calibri,'Segoe UI','Microsoft YaHei',sans-serif", richTextName: 'Calibri' },
  { label: 'Georgia', value: "Georgia,'Times New Roman',serif", richTextName: 'Georgia' },
  { label: 'Verdana', value: "Verdana,Geneva,Arial,sans-serif", richTextName: 'Verdana' },
  { label: 'Tahoma', value: "Tahoma,Verdana,Arial,sans-serif", richTextName: 'Tahoma' },
  {
    label: '黑体/无衬线（综合）',
    value: "'SimHei','Microsoft YaHei','PingFang SC',Arial,'Helvetica Neue',sans-serif",
    richTextName: 'SimHei'
  },
  { label: '等宽', value: "'Consolas','Courier New','NSimSun',monospace", richTextName: 'Consolas' }
]

/** 页面设置等：含「自动」 */
export const PRINT_FONT_FAMILY_FORM_OPTIONS = [
  { label: '自动（送货/派工偏宋体，其它偏雅黑）', value: '' },
  ...PRINT_FONT_FAMILY_PRESETS
]

/** 列/基本信息/页尾等：首项为跟随页面正文默认 */
export const PRINT_FONT_FAMILY_FIELD_OPTIONS = [
  { label: '同正文默认', value: '' },
  ...PRINT_FONT_FAMILY_PRESETS
]

/** 富文本页眉/页脚工具栏（与模板下拉主字体名一致） */
export const PRINT_FONT_FAMILY_RICH_TEXT_OPTIONS = [
  { label: '默认', value: '' },
  { label: '宋体', value: 'SimSun' },
  { label: '仿宋', value: 'FangSong' },
  { label: '楷体', value: 'KaiTi' },
  { label: '黑体', value: 'SimHei' },
  { label: '微软雅黑', value: 'Microsoft YaHei' },
  { label: '苹方', value: 'PingFang SC' },
  { label: 'Arial', value: 'Arial' },
  { label: 'Helvetica', value: 'Helvetica' },
  { label: 'Times New Roman', value: 'Times New Roman' },
  { label: 'Calibri', value: 'Calibri' },
  { label: 'Georgia', value: 'Georgia' },
  { label: 'Verdana', value: 'Verdana' },
  { label: 'Tahoma', value: 'Tahoma' },
  { label: '等宽', value: 'Consolas' }
]

/**
 * 未在模板中指定 page.defaultFontFamily 时，按打印场景给默认栈（偏实体打印可读性）。
 * @param {string} [templateType]
 */
export function defaultFontFamilyForTemplateType(templateType) {
  if (templateType === 'delivery_order' || templateType === 'dispatch_order') {
    return "'SimSun','NSimSun','Songti SC','STSong','Microsoft YaHei',serif"
  }
  return "'Microsoft YaHei','PingFang SC','Helvetica Neue',Arial,sans-serif"
}

/**
 * 模板页「正文」默认字体（表格/信息区/注意事项/签名/页脚等未单独覆盖时）。
 * @param {object} [page] template_config.page
 * @param {string} [templateType]
 */
export function effectiveBodyFontFamily(page, templateType) {
  const raw = page?.defaultFontFamily
  if (raw != null && String(raw).trim() !== '') return String(raw).trim()
  return defaultFontFamilyForTemplateType(templateType)
}

/**
 * 页眉区域默认字体（富文本内联字号仍保留；未设内联字体的文字继承此字体）。
 */
export function effectiveHeaderFontFamily(page, templateType) {
  const raw = page?.headerFontFamily
  if (raw != null && String(raw).trim() !== '') return String(raw).trim()
  return effectiveBodyFontFamily(page, templateType)
}

export function resolveBasicFieldFontFamily(field, page, templateType) {
  const raw = field?.fontFamily
  if (raw != null && String(raw).trim() !== '') return String(raw).trim()
  return effectiveBodyFontFamily(page, templateType)
}

export function resolveTableCellFontFamily(col, page, templateType) {
  const raw = col?.fontFamily
  if (raw != null && String(raw).trim() !== '') return String(raw).trim()
  return effectiveBodyFontFamily(page, templateType)
}

export function resolveLegacyTableFontFamily(page, templateType) {
  return effectiveBodyFontFamily(page, templateType)
}
